import React from 'react';

import cn from 'classnames';
import { bool, number, shape } from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { faAward } from '@fortawesome/pro-solid-svg-icons/faAward';
import { faBook } from '@fortawesome/pro-solid-svg-icons/faBook';
import { faDiploma } from '@fortawesome/pro-solid-svg-icons/faDiploma';

import Card from 'components/Common/Card';
import Flexbox from 'components/Common/Flexbox';
import FontAwesomeIcon from 'components/Common/FontAwesomeIcon';
import LazyLoadImage from 'components/Common/LazyLoadImage';
import Text from 'components/Common/Text';
import {
  CREDENTIAL_WALLET_DETAILS,
  CREDENTIAL_WALLET_PUBLIC_DETAILS,
} from 'routes/constants';
import { ensureObject } from 'utils/objects';

import Placeholder from 'modules/CredentialWallet/components/Placeholder';

import s from './CredentialCard.css';

const CredentialCard = ({ credential, studentId, isPublic }) => {
  const { t } = useTranslation();
  const {
    credentialName,
    credentialIssuerName,
    credentialAchievementType,
    credentialImage,
    credentialIsVerified,
    credentialCredentialType,
    id,
  } = ensureObject(credential);

  const isCourse = credentialAchievementType === 'Course';
  const isBadge = credentialAchievementType === 'Badge';

  const getIcon = () => {
    if (isCourse) return faBook;
    if (isBadge) return faAward;
    return faDiploma;
  };

  const href = isPublic
    ? CREDENTIAL_WALLET_PUBLIC_DETAILS({
        studentId,
        type: credentialCredentialType,
        credentialId: id,
      })
    : CREDENTIAL_WALLET_DETAILS({
        type: credentialCredentialType,
        credentialId: id,
      });

  return (
    <Link to={href}>
      <Card
        className={cn(s.card, {
          [s.course]: isCourse,
          [s.badge]: isBadge,
        })}
        component="div"
      >
        <div className={s.circle} />
        {credentialIsVerified && (
          <div className={s.verified}>
            <Text variant="tiny" color="white">
              {t('CredentialWallet.CredentialCard.Verified')}
            </Text>
          </div>
        )}
        <Flexbox
          className={s.content}
          p={1}
          rowGap={2}
          flexDirection="column"
          alignItems="center"
        >
          <div className={s.imageContainer}>
            {credentialImage ? (
              <LazyLoadImage
                className={s.logo}
                src={credentialImage}
                alt={credentialName}
              />
            ) : (
              <Placeholder width={96} height={96} isLoadingCredential={false} />
            )}
            <Flexbox
              justifyContent="center"
              alignItems="center"
              className={s.star}
            >
              <FontAwesomeIcon icon={getIcon()} color="white" size="md" />
            </Flexbox>
          </div>

          <div>
            <Text align="center" variant="titleSm" mb={0.5}>
              {credentialName}
            </Text>
            <Text align="center" variant="meta" color="grayDark">
              {t('CredentialWallet.CredentialCard.IssuedBy')}{' '}
              {credentialIssuerName}
            </Text>
          </div>
        </Flexbox>
      </Card>
    </Link>
  );
};

CredentialCard.propTypes = {
  credential: shape({}).isRequired,
  studentId: number,
  isPublic: bool,
};

CredentialCard.defaultProps = {
  studentId: null,
  isPublic: false,
};

export default CredentialCard;
